/**
 * 翡翠绿主题提供者组件
 * 统一管理整个应用的主题配置
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { ConfigProvider, App } from 'antd';
import { useTranslation } from 'react-i18next';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import zhTW from 'antd/locale/zh_TW';
import jaJP from 'antd/locale/ja_JP';
import deDE from 'antd/locale/de_DE';
import type { Locale } from 'antd/es/locale';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import 'dayjs/locale/de';
import { emeraldTheme, emeraldDarkTheme } from './emeraldTheme';
import './emeraldStyles.css';

// Ant Design locale mapping
const antdLocaleMap: Record<string, Locale> = {
  'zh-CN': zhCN,
  en: enUS,
  'zh-TW': zhTW,
  ja: jaJP,
  de: deDE,
};

// dayjs locale mapping
const dayjsLocaleMap: Record<string, string> = {
  'zh-CN': 'zh-cn',
  en: 'en',
  'zh-TW': 'zh-tw',
  ja: 'ja',
  de: 'de',
};

// 默认配置 dayjs 中文语言包
dayjs.locale('zh-cn');

// 主题上下文类型
interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
  currentTheme: typeof emeraldTheme;
}

// 创建主题上下文
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// 主题提供者属性
interface EmeraldThemeProviderProps {
  children: React.ReactNode;
}

// 主题提供者组件
export const EmeraldThemeProvider: React.FC<EmeraldThemeProviderProps> = ({
  children,
}) => {
  const { i18n } = useTranslation();

  // 主题状态管理
  const [isDark, setIsDark] = useState(false);

  // 根据 i18n 语言获取 Ant Design locale
  const antdLocale = useMemo(() => {
    const lang = i18n.language;
    // Try exact match first, then fallback
    if (antdLocaleMap[lang]) {
      return antdLocaleMap[lang];
    }
    // Try language without region (e.g., 'zh' -> 'zh-CN')
    const baseLang = lang.split('-')[0];
    if (baseLang === 'zh') {
      return zhCN;
    }
    return antdLocaleMap[baseLang] || enUS;
  }, [i18n.language]);

  // 同步 dayjs locale
  useEffect(() => {
    const lang = i18n.language;
    const dayjsLocale =
      dayjsLocaleMap[lang] || dayjsLocaleMap[lang.split('-')[0]] || 'en';
    dayjs.locale(dayjsLocale);
  }, [i18n.language]);

  // 从本地存储读取主题偏好
  useEffect(() => {
    const savedTheme = localStorage.getItem('emerald-theme');
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches;

    if (savedTheme) {
      setIsDark(savedTheme === 'dark');
    } else {
      setIsDark(prefersDark);
    }
  }, []);

  // 监听系统主题变化
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem('emerald-theme')) {
        setIsDark(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // 切换主题
  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    localStorage.setItem('emerald-theme', newTheme ? 'dark' : 'light');

    // 添加主题切换动画
    document.documentElement.style.setProperty(
      '--theme-transition',
      'all 0.3s ease'
    );
    setTimeout(() => {
      document.documentElement.style.removeProperty('--theme-transition');
    }, 300);
  };

  // 当前主题配置
  const currentTheme = isDark ? emeraldDarkTheme : emeraldTheme;

  // 主题上下文值
  const themeContextValue: ThemeContextType = {
    isDark,
    toggleTheme,
    currentTheme,
  };

  // 动态设置CSS变量
  useEffect(() => {
    const root = document.documentElement;

    if (isDark) {
      root.style.setProperty('--bg-primary', '#1f2937');
      root.style.setProperty('--bg-secondary', '#374151');
      root.style.setProperty('--text-primary', '#f3f4f6');
      root.style.setProperty('--text-secondary', '#d1d5db');
    } else {
      root.style.setProperty('--bg-primary', '#ffffff');
      root.style.setProperty('--bg-secondary', '#f9fafb');
      root.style.setProperty('--text-primary', '#1f2937');
      root.style.setProperty('--text-secondary', '#4b5563');
    }
  }, [isDark]);

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <ConfigProvider
        theme={currentTheme}
        locale={antdLocale}
        componentSize="middle"
        direction="ltr"
      >
        <App
          message={{ maxCount: 3 }}
          notification={{
            maxCount: 5,
            placement: 'topRight',
            duration: 4.5,
          }}
        >
          <div className={`emerald-theme-root ${isDark ? 'dark' : 'light'}`}>
            {children}
          </div>
        </App>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

// 主题钩子
export const useEmeraldTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useEmeraldTheme must be used within EmeraldThemeProvider');
  }
  return context;
};

// 主题切换按钮组件
export const ThemeToggleButton: React.FC<{
  className?: string;
  size?: 'small' | 'middle' | 'large';
}> = ({ className = '', size = 'middle' }) => {
  const { isDark, toggleTheme } = useEmeraldTheme();

  return (
    <button
      className={`theme-toggle-btn ${className}`}
      onClick={toggleTheme}
      aria-label={isDark ? '切换到浅色主题' : '切换到深色主题'}
      style={{
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(16, 185, 129, 0.2)',
        borderRadius: '8px',
        padding: size === 'small' ? '6px' : size === 'large' ? '12px' : '8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontSize:
          size === 'small' ? '14px' : size === 'large' ? '18px' : '16px',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
        e.currentTarget.style.transform = 'scale(1.05)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      {isDark ? '🌙' : '☀️'}
    </button>
  );
};

// 主题状态指示器
export const ThemeIndicator: React.FC = () => {
  const { isDark } = useEmeraldTheme();

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        background: 'rgba(16, 185, 129, 0.9)',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '500',
        backdropFilter: 'blur(8px)',
        zIndex: 1000,
        transition: 'all 0.3s ease',
      }}
    >
      {isDark ? '🌙 深色模式' : '☀️ 浅色模式'}
    </div>
  );
};

export default EmeraldThemeProvider;
